import { useEffect, useMemo, useState } from "react";
import { Card, Modal } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { GridSeries, useApi } from "../apiService";
import { useLanguage } from "../../../i18n/LanguageProvider";
import { track } from "../../../utils/track";
import "./gridMix.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

// Stable colour + draw order for the supply mix (bottom→top of the stack).
const FUEL_ORDER = ["hydro", "nuclear", "wind", "solar", "thermal", "other"];
const FUEL_COLOR: Record<string, string> = {
  hydro: "#2563eb",
  nuclear: "#7c3aed",
  wind: "#10b981",
  solar: "#f59e0b",
  thermal: "#ef4444",
  other: "#a1a1aa",
};
const FUEL_LABEL_SV: Record<string, string> = {
  hydro: "Vattenkraft",
  nuclear: "Kärnkraft",
  wind: "Vindkraft",
  solar: "Solkraft",
  thermal: "Termisk",
  other: "Övrigt",
};
const FUEL_LABEL_EN: Record<string, string> = {
  hydro: "Hydro",
  nuclear: "Nuclear",
  wind: "Wind",
  solar: "Solar",
  thermal: "Thermal",
  other: "Other",
};

type Props = { area: string };

const CARD_HOURS = 36;
const MODAL_RANGES = [36, 7 * 24];

// Generation by fuel, load and the latest reservoir level for one zone over the
// last `hours`, shaped for a stacked Chart.js line chart. Fetches only while
// `enabled` (the modal loads its own range when opened).
function useGridMix(zone: string | null, hours: number, enabled = true) {
  const api = useApi();
  const { language } = useLanguage();
  const [generation, setGeneration] = useState<GridSeries[]>([]);
  const [load, setLoad] = useState<GridSeries[]>([]);
  const [reservoir, setReservoir] = useState<{ value: number; unit: string; t: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => document.documentElement.getAttribute("data-bs-theme") === "dark",
  );

  useEffect(() => {
    const observer = new MutationObserver(() =>
      setDarkMode(document.documentElement.getAttribute("data-bs-theme") === "dark"),
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!zone || !enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    const from = new Date(Date.now() - hours * 3600 * 1000).toISOString();
    const to = new Date().toISOString();
    Promise.all([
      api.fetchGridSeries(zone, "generation", from, to),
      api.fetchGridSeries(zone, "load", from, to),
      api.fetchGridSeries(zone, "reservoir"),
    ])
      .then(([gen, ld, res]) => {
        if (cancelled) return;
        setGeneration(gen.series || []);
        setLoad(ld.series || []);
        const r = (res.series || [])[0];
        const last = r?.points?.[r.points.length - 1];
        setReservoir(last ? { value: last.v, unit: r.unit, t: last.t } : null);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zone, hours, enabled]);

  const chartData = useMemo(() => {
    if (!generation.length) return null;
    const fuelLabel = language === "sv" ? FUEL_LABEL_SV : FUEL_LABEL_EN;
    // ENTSO-E's newest load values are provisional and often far off (SE2 ~4×
    // too high, SE3 a third too low) until revised an hour or two later. Load
    // lags ~2 h itself, so cut two hours before the newest load point (not now)
    // and end every line there, so generation and load reach the same hour.
    const loadPoints = (load[0]?.points ?? []).map((p) => [new Date(p.t).getTime(), p.v] as const);
    const cutoff = loadPoints.length
      ? Math.max(...loadPoints.map(([t]) => t)) - 2 * 3600_000
      : Infinity;

    // Union of all timestamps across fuels, sorted, up to the cutoff.
    const tset = new Set<number>();
    generation.forEach((s) => s.points.forEach((p) => tset.add(new Date(p.t).getTime())));
    const times = Array.from(tset).filter((t) => t <= cutoff).sort((a, b) => a - b);

    const byFuel = new Map(generation.map((s) => [s.fuel, new Map(s.points.map((p) => [new Date(p.t).getTime(), p.v]))]));
    const present = FUEL_ORDER.filter((f) => byFuel.has(f));

    const genDatasets = present.map((fuel) => ({
      label: fuelLabel[fuel] ?? fuel,
      data: times.map((t) => byFuel.get(fuel)!.get(t) ?? null),
      borderColor: FUEL_COLOR[fuel] ?? "#a1a1aa",
      backgroundColor: (FUEL_COLOR[fuel] ?? "#a1a1aa") + "cc",
      borderWidth: 1,
      pointRadius: 0,
      fill: true,
      stack: "gen",
      tension: 0.25,
      yAxisID: "y",
      // Higher order = drawn first = underneath. Keeps the filled stack below the
      // load line (which uses a lower order to sit on top).
      order: 1,
    }));

    const loadMap = load[0]
      ? new Map(loadPoints.filter(([t]) => t <= cutoff))
      : null;
    const loadDataset = loadMap
      ? [
          {
            label: language === "sv" ? "Förbrukning" : "Load",
            data: times.map((t) => loadMap.get(t) ?? null),
            // Theme-aware: near-black is invisible in dark mode.
            borderColor: darkMode ? "#fafafa" : "#18181b",
            backgroundColor: "transparent",
            borderWidth: 2,
            borderDash: [5, 3],
            pointRadius: 0,
            fill: false,
            tension: 0.25,
            // Load is often a coarser resolution than generation (e.g. hourly vs
            // 15-min), so most timestamps on the generation grid have no load
            // value. Without spanGaps those nulls break the line into invisible
            // isolated points; spanGaps connects the real points into one line.
            spanGaps: true,
            // Lower order than the stack = drawn last = on top of the (opaque)
            // fills, so the line stays visible even with every fuel active.
            order: 0,
            // Own un-stacked axis so the line sits at its true MW value and draws
            // over the stack — otherwise in export-heavy zones (SE1/SE2) where load
            // is well below total generation it disappears behind the filled areas.
            yAxisID: "yLoad",
          },
        ]
      : [];

    // Shared axis ceiling: the tallest stacked-generation total (and load), so the
    // stacked axis and the load overlay axis map MW to the same pixels.
    let peak = 0;
    times.forEach((t) => {
      let total = 0;
      present.forEach((f) => { total += byFuel.get(f)!.get(t) ?? 0; });
      if (total > peak) peak = total;
      const lv = loadMap?.get(t) ?? 0;
      if (lv > peak) peak = lv;
    });
    const axisMax = peak > 0 ? Math.ceil((peak * 1.05) / 100) * 100 : undefined;

    const labels = times.map((t) =>
      new Date(t).toLocaleString(language === "sv" ? "sv-SE" : "en-GB", {
        timeZone: "Europe/Stockholm",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
    );
    return { labels, datasets: [...genDatasets, ...loadDataset], axisMax };
  }, [generation, load, language, darkMode]);

  const axisMax = chartData?.axisMax;
  const options = useMemo(() => {
    const tickColor = darkMode ? "#a1a1aa" : "#52525b";
    const gridColor = darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: { boxWidth: 12, font: { size: 11 }, color: tickColor },
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) =>
              `${ctx.dataset.label}: ${ctx.parsed.y == null ? "–" : Math.round(ctx.parsed.y)} MW`,
          },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { maxTicksLimit: 8, font: { size: 10 }, color: tickColor } },
        y: {
          stacked: true,
          beginAtZero: true,
          max: axisMax,
          title: { display: true, text: "MW", color: tickColor },
          grid: { color: gridColor },
          ticks: { font: { size: 10 }, color: tickColor },
        },
        // Invisible overlay axis for the load line — same 0..axisMax range as `y`
        // but not stacked, so the line is positioned by its real value and floats
        // on top of the generation stack.
        yLoad: {
          stacked: false,
          beginAtZero: true,
          max: axisMax,
          display: false,
          grid: { display: false },
        },
      },
    };
  }, [darkMode, axisMax]);

  return { chartData, options, reservoir, loading, error };
}

// Production-mix panel: stacked generation by fuel + the load line, for one zone.
// Hidden for the aggregate "ALL" view (grid data is per bidding zone). Clicking
// the card opens the chart large, with a longer range to choose.
export const GridMix = ({ area }: Props) => {
  const { language } = useLanguage();
  const zone = area && area !== "ALL" ? area : null;
  const card = useGridMix(zone, CARD_HOURS);
  const [open, setOpen] = useState(false);
  const [modalHours, setModalHours] = useState(CARD_HOURS);
  const big = useGridMix(zone, modalHours, open);
  const sv = language === "sv";

  if (!zone) return null;

  const openModal = () => {
    setOpen(true);
    track("modal_open", "grid_mix", { zone });
  };

  const title = sv ? `Produktionsmix · ${zone}` : `Generation mix · ${zone}`;
  const reservoirLabel = (r: { value: number } | null) =>
    r ? (
      <span className="grid-mix-reservoir">
        {sv ? "Vattenmagasin" : "Hydro reservoir"}: <strong>{(r.value / 1_000_000).toFixed(1)} TWh</strong>
      </span>
    ) : null;
  const canvas = (g: ReturnType<typeof useGridMix>) => (
    <>
      {g.loading && <div className="grid-mix-empty">{sv ? "Laddar…" : "Loading…"}</div>}
      {!g.loading && (g.error || !g.chartData) && (
        <div className="grid-mix-empty">{sv ? "Ingen produktionsdata tillgänglig." : "No generation data available."}</div>
      )}
      {!g.loading && !g.error && g.chartData && <Line data={g.chartData} options={g.options as any} />}
    </>
  );
  const rangeLabel = (h: number) => (h < 48 ? `${h} h` : sv ? `${h / 24} dygn` : `${h / 24} days`);

  return (
    <>
      <Card
        className="shadow-card h-100 grid-mix-card"
        role="button"
        tabIndex={0}
        aria-label={sv ? `${title}, öppna större` : `${title}, open larger`}
        onClick={openModal}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openModal();
          }
        }}
      >
        <Card.Body>
          <div className="grid-mix-head">
            <h3>{title}</h3>
            {reservoirLabel(card.reservoir)}
            <span className="grid-mix-expand" aria-hidden>
              ⤢
            </span>
          </div>
          <div className="grid-mix-canvas">{canvas(card)}</div>
        </Card.Body>
      </Card>

      <Modal show={open} onHide={() => setOpen(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title as="h3" className="grid-mix-modal-title">
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="grid-mix-head">
            <div className="grid-mix-ranges" role="tablist">
              {MODAL_RANGES.map((h) => (
                <button
                  key={h}
                  type="button"
                  role="tab"
                  aria-selected={h === modalHours}
                  className={h === modalHours ? "is-active" : ""}
                  onClick={() => {
                    setModalHours(h);
                    track("feature_use", `grid_mix_range_${h}h`, { zone });
                  }}
                >
                  {rangeLabel(h)}
                </button>
              ))}
            </div>
            {reservoirLabel(big.reservoir)}
          </div>
          <div className="grid-mix-canvas grid-mix-canvas--large">{canvas(big)}</div>
        </Modal.Body>
      </Modal>
    </>
  );
};
